'use client'

import { m, useScroll, useTransform } from 'framer-motion'
import type { LucideIcon } from 'lucide-react'
import {
  ArrowRight,
  Building,
  Camera,
  Code2,
  Database, // Web
  Edit,
  FileSearch,
  Lightbulb,
  Link, // SEO
  List,
  Map,
  Monitor,
  Rocket,
  Search,
  ShoppingCart,
  Target,
  Users, // SMM
} from 'lucide-react'
import { useRef } from 'react'

type ProcessStep = {
  number?: string
  title: string
  subtitle: string
  description: string
  icon?: string | LucideIcon
  gradient?: string
  bgGradient?: string
}

// Map string names to components
const iconMap: Record<string, LucideIcon> = {
  FileSearch: FileSearch,
  Lightbulb: Lightbulb,
  Code2: Code2,
  Rocket: Rocket,
  Map: Map,
  Camera: Camera,
  Target: Target,
  Users: Users,
  Search: Search,
  List: List,
  Edit: Edit,
  Link: Link,
  Monitor: Monitor,
  ShoppingCart: ShoppingCart,
  Building: Building,
  Database: Database,
}

const steps: ProcessStep[] = [
  {
    number: '01',
    title: 'Исследуем',
    subtitle: 'Бриф и аналитика',
    description:
      'Глубоко погружаемся в ваш бизнес, изучаем конкурентов и целевую аудиторию. На выходе — чёткое ТЗ и понимание, как победить на рынке.',
    icon: FileSearch,
    gradient: 'from-blue-500 to-cyan-400',
    bgGradient: 'from-blue-600/20 to-cyan-500/10',
  },
  {
    number: '02',
    title: 'Проектируем',
    subtitle: 'Дизайн и прототипы',
    description:
      'Создаём прототипы и продуманный дизайн. Показываем, согласовываем, дорабатываем — пока не получим идеальный результат.',
    icon: Lightbulb,
    gradient: 'from-purple-500 to-pink-500',
    bgGradient: 'from-purple-600/20 to-pink-500/10',
  },
  {
    number: '03',
    title: 'Разрабатываем',
    subtitle: 'Код и тестирование',
    description:
      'Пишем чистый код, тестируем на всех устройствах. Каждую неделю — демо и отчёт о прогрессе. Вы всегда в курсе.',
    icon: Code2,
    gradient: 'from-blue-500 to-cyan-400',
    bgGradient: 'from-blue-600/20 to-cyan-500/10',
  },
  {
    number: '04',
    title: 'Запускаем',
    subtitle: 'Запуск и рост',
    description:
      'Выводим продукт в работу, настраиваем аналитику и рекламу. Остаёмся на связи и помогаем масштабироваться.',
    icon: Rocket,
    gradient: 'from-emerald-500 to-teal-400',
    bgGradient: 'from-emerald-600/20 to-teal-500/10',
  },
]

interface ProcessProps {
  initialSteps?: ProcessStep[]
  kicker?: string
  title?: string
  highlight?: string
  ctaNote?: string
  ctaHighlight?: string
  ctaText?: string
  ctaHref?: string
}

export default function Process({
  initialSteps,
  kicker = 'Процесс',
  title = 'Как мы создаём',
  highlight = 'успешные продукты',
  ctaNote = 'Готовы начать? В среднем от первого звонка до запуска — ',
  ctaHighlight = '4-6 недель',
  ctaText = 'Обсудить проект',
  ctaHref = '/contacts',
}: ProcessProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const displaySteps = initialSteps && initialSteps.length > 0 ? initialSteps : steps

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  })

  const lineWidth = useTransform(scrollYProgress, [0.2, 0.8], ['0%', '100%'])

  return (
    <section ref={containerRef} className="home-section relative overflow-hidden">
      <div className="home-container relative z-10">
        {/* Header */}
        <div className="mb-12 text-center md:mb-24">
          <m.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="home-kicker mb-6 text-purple-400"
          >
            {kicker}
          </m.p>

          <m.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="home-title mb-8"
          >
            {title}
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-emerald-400 bg-clip-text text-transparent">
              {highlight}
            </span>
          </m.h2>
        </div>

        {/* Timeline Line - Animated */}
        <div className="relative mx-20 mb-16 hidden h-1 lg:block">
          <div className="absolute inset-0 rounded-full bg-white/5" />
          <m.div
            style={{ width: lineWidth }}
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500"
          />
        </div>

        {/* Steps */}
        <div className="relative">
          <div className="flex flex-col gap-6 md:grid md:grid-cols-2 md:gap-8 xl:grid-cols-4 xl:gap-6">
            {displaySteps.map((step, index) => (
              <m.div
                key={step.number || index}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ delay: index * 0.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="group relative w-full md:min-w-0"
              >
                {/* Connection Arrow */}
                {index < displaySteps.length - 1 && (
                  <div className="absolute top-1/2 -right-3 z-20 hidden -translate-y-1/2 lg:flex">
                    <ArrowRight className="h-6 w-6 text-white/20" />
                  </div>
                )}

                {/* Card */}
                <div className="relative h-full overflow-hidden rounded-[32px] transition-all duration-500 group-hover:scale-[1.02]">
                  {/* Background Gradient */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${step.bgGradient || 'from-blue-600/20 to-cyan-500/10'} opacity-0 transition-opacity duration-500 group-hover:opacity-100`}
                  />

                  {/* Glass Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl" />

                  {/* Border */}
                  <div className="absolute inset-0 rounded-[32px] border border-white/10 transition-colors duration-500 group-hover:border-white/20" />

                  {/* Glow */}
                  <div
                    className={`absolute -inset-[100px] bg-gradient-to-br ${step.gradient || 'from-blue-500 to-cyan-400'} rounded-full opacity-0 blur-[120px] transition-opacity duration-700 group-hover:opacity-20`}
                  />

                  {/* Content */}
                  <div className="relative z-10 p-8 lg:p-10">
                    {/* Big Number Background */}
                    <div
                      className={`absolute -top-10 -right-4 bg-gradient-to-br text-[180px] leading-none font-black ${step.gradient || 'from-blue-500 to-cyan-400'} bg-clip-text text-transparent opacity-[0.07] select-none`}
                    >
                      {step.number}
                    </div>

                    {/* Icon */}
                    <m.div
                      whileHover={{ rotate: 5, scale: 1.1 }}
                      className={`h-20 w-20 rounded-3xl bg-gradient-to-br ${step.gradient || 'from-blue-500 to-cyan-400'} mb-8 p-[2px] shadow-xl`}
                    >
                      <div className="flex h-full w-full items-center justify-center rounded-3xl bg-[#0a0f1a]">
                        {/* Dynamic Icon Handling */}
                        {(() => {
                          const IconComponent: LucideIcon =
                            typeof step.icon === 'string'
                              ? iconMap[step.icon] || Code2
                              : step.icon || Code2

                          return (
                            <IconComponent className="h-10 w-10 text-white" strokeWidth={1.5} />
                          )
                        })()}
                      </div>
                    </m.div>

                    {/* Number Badge */}
                    <div
                      className={`inline-flex items-center gap-2 rounded-full bg-gradient-to-r px-4 py-1.5 ${step.gradient || 'from-blue-500 to-cyan-400'} mb-6`}
                    >
                      <span className="text-sm font-bold text-white">Шаг {step.number}</span>
                    </div>

                    {/* Title */}
                    <h3 className="home-card-title mb-2">{step.title}</h3>
                    <p className="home-caption mb-4 text-gray-400">{step.subtitle}</p>

                    {/* Description */}
                    <p className="home-subnote text-gray-400">{step.description}</p>
                  </div>
                </div>
              </m.div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <m.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center md:mt-20"
        >
          <p className="home-lead mb-8 text-gray-400">
            {ctaNote}
            {ctaHighlight ? <span className="font-semibold text-white">{ctaHighlight}</span> : null}
          </p>
          <a
            href={ctaHref}
            className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-10 py-5 text-lg font-semibold transition-all duration-300 hover:shadow-[0_0_50px_rgba(168,85,247,0.4)]"
          >
            {ctaText}
            <ArrowRight className="h-5 w-5" />
          </a>
        </m.div>
      </div>
    </section>
  )
}
